import AnalyticsTracker from './interface';
import func from '../../func';

/**
 * Tracks events with Google Analytics via gtag.js.
 *
 * @constructor
 * @augments AnalyticsTracker
 */
export default function GtagTracker(window, config) {
    AnalyticsTracker.call(this, window, config);

    /**
     * @param {string} query The user's query as a URL query string.
     * @param {string} filter The last selected filter as a URL query string.
     * @param {function} [callback] Called once the request has been tracked, tracking failed, or timed out.
     *      It can be assumed that this function is called always. No arguments are passed.
     */
    this.trackSearch = function (query, filter, callback) {
        callback = func.undefinedToNoOp(callback);
        let searchId = AnalyticsTracker.SEARCH_PATH + query;

        if (typeof filter !== 'undefined') {
            searchId += '&' + filter;
        }

        // Universal Analytics:
        const uaPromise = new Promise((resolve) => {
            this.window.gtag('event', 'page_view', {
                page_path: searchId,
                event_callback: func.wrapCallbackWithTimeout(resolve, AnalyticsTracker.TIMEOUT),
            });
        });

        // Google Analytics 4:
        const ga4Promise = new Promise((resolve) => {
            const plainQuery = query.split('=')[1];
            this.window.gtag('event', 'view_search_results', {
                search_term: plainQuery,
                event_callback: func.wrapCallbackWithTimeout(resolve, AnalyticsTracker.TIMEOUT),
            });
        });

        return Promise.all([ga4Promise, uaPromise]).then(callback);
    };

    /**
     * @param {string} event The type of event, usually something like "findologicEvent".
     * @param {string} category Thee event category, e.g. "FINDOLOGIC Click".
     * @param {string} action The user interaction that caused this event, e.g. "Search-Concept".
     * @param {string} label The specific thing the user interacted with.
     * @param {function} [callback] Called once the request has been tracked, tracking failed, or timed out.
     *      It can be assumed that this function is called always. No arguments are passed.
     */
    this.trackEvent = function (event, category, action, label, callback) {
        callback = func.undefinedToNoOp(callback);

        this.window.gtag('event', event, {
            event_category: category,
            event_action: action,
            event_label: label,
            event_callback: func.wrapCallbackWithTimeout(callback, AnalyticsTracker.TIMEOUT),
        });
    };

    /**
     * Determines whether the prerequisites for using this tracker implementation are cleared
     * @returns {boolean}
     */
    this.isAvailable = function () {
        return typeof this.window.gtag !== 'undefined';
    };
}
